/* ==========================================================================
   WNM Audio — sound grid
   A grid of circular sound toggles. Only one sound plays at a time.
   Optional category tabs (WAI-ARIA tablist) switch between grids.
   ========================================================================== */

class WnmAudioV2 extends HTMLElement {
  constructor() {
    super();
    this.currentButton = null;
    this.boundTabKeydown = this.onTabKeydown.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    if (this.initialized) return;

    this.sectionId = this.dataset.sectionId;

    // i18n templates (with %name% placeholder) from data-* attributes.
    this.i18n = {
      play: this.dataset.labelPlay || 'Play %name%',
      pause: this.dataset.labelPause || 'Pause %name%',
      playing: this.dataset.statusPlaying || 'Playing %name%',
      stopped: this.dataset.statusStopped || 'Stopped',
    };

    this.soundButtons = Array.from(this.querySelectorAll('.wnm-audio-v2__sound'));
    this.tabs = Array.from(this.querySelectorAll('.wnm-audio-v2__tab'));
    this.panels = Array.from(this.querySelectorAll('.wnm-audio-v2__grid'));
    this.audios = Array.from(this.querySelectorAll('audio[id^="WNMAudio-audio-"]'));
    this.statusRegion = this.querySelector('[data-status]');

    this.soundButtons.forEach((button) => {
      button.addEventListener('click', () => this.onSoundClick(button));
    });

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab));
      tab.addEventListener('keydown', this.boundTabKeydown);
    });

    this.initialized = true;
  }

  destroy() {
    this.stopAll();
    if (this.tabs) {
      this.tabs.forEach((tab) => tab.removeEventListener('keydown', this.boundTabKeydown));
    }
    this.initialized = false;
  }

  /* ---- playback --------------------------------------------------------- */

  stopAll() {
    if (this.audios) {
      this.audios.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
    }
    if (this.soundButtons) {
      this.soundButtons.forEach((button) => this.setButtonState(button, false));
    }
    this.currentButton = null;
  }

  onSoundClick(button) {
    // Toggle off if the same button is clicked again.
    if (button === this.currentButton) {
      this.stopAll();
      this.announce(this.i18n.stopped);
      return;
    }

    this.stopAll();

    const targetId = button.dataset.audioTarget;
    const audio = targetId ? document.getElementById(targetId) : null;

    this.setButtonState(button, true);
    this.currentButton = button;

    if (audio) {
      audio.volume = 0.5;
      audio.play().catch(() => { });
    }

    this.announce(this.format(this.i18n.playing, button.dataset.soundName));
  }

  setButtonState(button, active) {
    button.classList.toggle('wnm-audio-v2__sound--active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const template = active ? this.i18n.pause : this.i18n.play;
    button.setAttribute('aria-label', this.format(template, button.dataset.soundName));
  }

  /* ---- tabs ------------------------------------------------------------- */

  activateTab(tab) {
    const index = tab.dataset.tabIndex;

    // Switching category stops any playing sound.
    this.stopAll();

    this.tabs.forEach((item) => {
      const isActive = item === tab;
      item.classList.toggle('wnm-audio-v2__tab--active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      item.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.tabIndex === index;
      panel.classList.toggle('wnm-audio-v2__grid--active', isActive);
    });
  }

  onTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.tabs[nextIndex];
    nextTab.focus();
    this.activateTab(nextTab);
  }

  /* ---- helpers ---------------------------------------------------------- */

  format(template, name) {
    return String(template).replace('%name%', name || '');
  }

  announce(message) {
    if (this.statusRegion) {
      this.statusRegion.textContent = message;
    }
  }
}

if (customElements.get('wnm-audio-v2') === undefined) {
  customElements.define('wnm-audio-v2', WnmAudioV2);
}

/* Theme editor support ----------------------------------------------------- */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target.querySelector('wnm-audio-v2');
    if (el && typeof el.init === 'function') {
      el.initialized = false;
      el.init();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const el = event.target.querySelector('wnm-audio-v2');
    if (el && typeof el.destroy === 'function') {
      el.destroy();
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const el = event.target.closest('wnm-audio-v2');
    if (!el) return;
    // If the selected block is a category, its element IS the tab button.
    let tab = null;
    if (event.target.classList && event.target.classList.contains('wnm-audio-v2__tab')) {
      tab = event.target;
    } else if (event.target.querySelector) {
      tab = event.target.querySelector('.wnm-audio-v2__tab');
    }
    if (tab && typeof el.activateTab === 'function') {
      el.activateTab(tab);
    }
    event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}
